import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Typography } from 'antd';
import * as yup from 'yup';

import { CheckBoxForm } from '@/components/form/CheckBox';
import { InputForm } from '@/components/form/Input';
import { PasswordForm } from '@/components/form/Password';
import { LoadingStatus } from '@/core/enums/loadingStatus';
import { useAppDispatch, useAppSelector } from '@/core/redux/hooks';
import { authSelectors } from '@/core/redux/slices/auth/selectors';
import { authActions } from '@/core/redux/slices/auth/slice';
import { usersSelectors } from '@/core/redux/slices/users/selectors';

const schema = yup.object().shape({
  email: yup
    .string()
    .email('Неправильный формат почты')
    .required('Поле обязательно для заполнения'),
  password: yup.string().required('Поле обязательно для заполнения'),
  passwordConfirmation: yup
    .string()
    .required('Поле обязательно для заполнения')
    .oneOf([yup.ref('password')], 'Пароли должны совпадать'),
  cons_to_receive_mailings: yup.boolean().default(true).required(),
  is_agreed_conf_policy: yup.boolean().default(true).required().oneOf([true]),
  cons_to_processing_pers_data: yup
    .boolean()
    .default(true)
    .required()
    .oneOf([true]),
});

interface ISignUpForm {
  email: string;
  password: string;
  passwordConfirmation: string;
  cons_to_receive_mailings: boolean;
  is_agreed_conf_policy: boolean;
  cons_to_processing_pers_data: boolean;
}

export const SignUpPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const registerLock = useAppSelector(authSelectors.registerLock);
  const user = useAppSelector(usersSelectors.user);

  const methods = useForm<ISignUpForm>({
    resolver: yupResolver(schema),
    defaultValues: {
      cons_to_receive_mailings: true,
      is_agreed_conf_policy: true,
      cons_to_processing_pers_data: true,
    },
  });
  const { Text } = Typography;

  const onSubmit = (data: ISignUpForm) => {
    dispatch(
      authActions.register({
        email: data.email,
        password: data.password,
        cons_to_receive_mailings: data.cons_to_receive_mailings,
        is_agreed_conf_policy: data.is_agreed_conf_policy,
        cons_to_processing_pers_data: data.cons_to_processing_pers_data,
      })
    );
  };

  useEffect(() => {
    if (registerLock === LoadingStatus.LOADED) {
      navigate('/auth/confirm');
    }
  }, [registerLock]);

  useEffect(() => {
    return () => {
      dispatch(authActions.setRegisterLock(LoadingStatus.NEVER));
    };
  }, []);

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div className='flex flex-col gap-6'>
          <div className='flex flex-col gap-2'>
            <InputForm name='email' placeholder='Ваш e-mail' size='large' />
            <PasswordForm
              name='password'
              placeholder='Придумайте пароль'
              size='large'
            />
            <PasswordForm
              name='passwordConfirmation'
              placeholder='Повторите пароль'
              size='large'
            />
          </div>
          <div className='flex flex-col gap-2'>
            <div className='flex items-center gap-2'>
              <CheckBoxForm
                name='cons_to_receive_mailings'
                label={
                  <Text type='secondary' className='text-xs'>
                    Даю{' '}
                    <Link
                      to='https://storage.yandexcloud.net/aeropro-static/agreement_ad_aeropro_expert.pdf'
                      target='_blank'
                      rel='noreferrer'
                    >
                      <Text className='text-xs text-sky-700 hover:underline underline-offset-1'>
                        согласие
                      </Text>
                    </Link>{' '}
                    на получение рекламы и рассылок о скидках
                  </Text>
                }
              />
            </div>

            <div className='flex items-center gap-2'>
              <CheckBoxForm
                name='is_agreed_conf_policy'
                label={
                  <Text type='secondary' className='text-xs'>
                    Подтверждаю, что ознакомлен(а) с{' '}
                    <Link
                      to='https://storage.yandexcloud.net/aeropro-static/privacypolicy_%20aeropro_expert.pdf'
                      target='_blank'
                      rel='noreferrer'
                    >
                      <Text className='text-xs text-sky-700 hover:underline underline-offset-1'>
                        Политикой конфиденциальности
                      </Text>
                    </Link>
                  </Text>
                }
              />
            </div>

            <div className='flex items-center gap-2'>
              <CheckBoxForm
                name='cons_to_processing_pers_data'
                label={
                  <Text type='secondary' className='text-xs'>
                    Даю согласие на{' '}
                    <Link
                      to='https://storage.yandexcloud.net/aeropro-static/user_agreement_aeropro_expert.pdf'
                      target='_blank'
                      rel='noreferrer'
                    >
                      <Text className='text-xs text-sky-700 hover:underline underline-offset-1'>
                        обработку персональных данных
                      </Text>
                    </Link>
                  </Text>
                }
              />
            </div>

            <Button
              htmlType='submit'
              type='primary'
              block
              loading={registerLock === LoadingStatus.LOADING}
              disabled={!methods.formState.isValid}
            >
              Получить код подтверждения
            </Button>
            <div className='text-center'>
              <Text type='secondary' className='text-xs'>
                Создавая аккаунт, вы соглашаетесь с{' '}
                <Link
                  to='https://storage.yandexcloud.net/aeropro-static/terms.pdf'
                  target='_blank'
                  rel='noreferrer'
                >
                  <Text className='text-xs text-sky-700 hover:underline underline-offset-1'>
                    Офертой
                  </Text>
                </Link>
              </Text>
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
